import { useEffect, useMemo, useRef, useState } from 'react';
import type { Incident, Unit } from '../lib/types';
import { elapsed } from '../lib/format';

/**
 * Situational map.
 *
 * A raster basemap under markers this component projects itself. Tiles come
 * from whatever `map.tileUrl` points at — OpenStreetMap by default, an internal
 * tile server on a closed network.
 *
 * The grid is drawn underneath the tiles rather than replaced by them, so a
 * control room that cannot reach the tile server still gets a working relative
 * picture: who is where, how far apart, which incident has nobody near it. The
 * map degrades instead of going blank.
 *
 * Note that requesting tiles tells the tile host which areas are being looked
 * at. On OpenStreetMap that is an acceptable trade for a usable map; for a
 * deployment where that matters, self-host and change the URL.
 */

const TILE_SIZE = 256;

interface Point {
  x: number;
  y: number;
}

/** Visible tiles for the current viewport, in screen coordinates. */
function visibleTiles(
  topLeftWorld: Point,
  width: number,
  height: number,
  zoom: number,
): { key: string; x: number; y: number; tx: number; ty: number; z: number }[] {
  const max = 2 ** zoom;
  const first = { x: Math.floor(topLeftWorld.x / TILE_SIZE), y: Math.floor(topLeftWorld.y / TILE_SIZE) };
  const last = {
    x: Math.floor((topLeftWorld.x + width) / TILE_SIZE),
    y: Math.floor((topLeftWorld.y + height) / TILE_SIZE),
  };

  const tiles = [];
  for (let ty = first.y; ty <= last.y; ty += 1) {
    // Vertical wrap has no meaning; horizontal does, so x is taken modulo world.
    if (ty < 0 || ty >= max) continue;
    for (let tx = first.x; tx <= last.x; tx += 1) {
      const wrapped = ((tx % max) + max) % max;
      tiles.push({
        key: `${zoom}/${tx}/${ty}`,
        x: tx * TILE_SIZE - topLeftWorld.x,
        y: ty * TILE_SIZE - topLeftWorld.y,
        tx: wrapped,
        ty,
        z: zoom,
      });
    }
  }
  return tiles;
}

function project(latitude: number, longitude: number, zoom: number): Point {
  const scale = TILE_SIZE * 2 ** zoom;
  const x = ((longitude + 180) / 360) * scale;
  const sinLat = Math.sin((latitude * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
}

/** Metres per pixel at this latitude and zoom — used for the scale bar. */
function metresPerPixel(latitude: number, zoom: number): number {
  return (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;
}

export interface MapMarker {
  id: string;
  kind: 'unit' | 'incident';
  latitude: number;
  longitude: number;
  label: string;
  color: string;
  detail: string;
  selected?: boolean;
  muted?: boolean;
}

export function MapView({
  units,
  incidents,
  center,
  selectedId,
  onSelect,
  height = 420,
  tileUrl,
  attribution,
}: {
  units: Unit[];
  incidents: Incident[];
  center: { latitude: number; longitude: number };
  selectedId?: string | null;
  onSelect?: (kind: 'unit' | 'incident', id: string) => void;
  height?: number;
  /** Omit to draw the grid alone — useful on a network with no tile server. */
  tileUrl?: string | null;
  attribution?: string | null;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 720, height });
  const [zoom, setZoom] = useState(12);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [height]);

  const markers = useMemo<MapMarker[]>(() => {
    const out: MapMarker[] = [];
    for (const incident of incidents) {
      if (incident.latitude == null || incident.longitude == null) continue;
      out.push({
        id: incident.id,
        kind: 'incident',
        latitude: incident.latitude,
        longitude: incident.longitude,
        label: incident.incident_no.split('-').pop() ?? '',
        color: incident.priority_color ?? '#6b7280',
        detail: `${incident.incident_no} · ${incident.category_name ?? 'Unclassified'} · ${incident.location_address ?? 'No address'}`,
        selected: incident.id === selectedId,
        muted: ['closed', 'cancelled', 'resolved'].includes(incident.status),
      });
    }
    for (const unit of units) {
      if (unit.latitude == null || unit.longitude == null) continue;
      out.push({
        id: unit.id,
        kind: 'unit',
        latitude: unit.latitude,
        longitude: unit.longitude,
        label: unit.call_sign,
        color:
          unit.status === 'available'
            ? '#22c55e'
            : unit.status === 'out_of_service' || unit.status === 'off_duty'
              ? '#6b7280'
              : '#3b82f6',
        detail: `${unit.call_sign} — ${unit.name} · ${unit.status.replace('_', ' ')} · updated ${elapsed(unit.location_updated_at ?? unit.status_updated_at)} ago`,
        selected: unit.id === selectedId,
        muted: unit.status === 'off_duty',
      });
    }
    return out;
  }, [units, incidents, selectedId]);

  const origin = project(center.latitude, center.longitude, zoom);

  // The world-pixel coordinate of the viewport's top-left corner: everything
  // the basemap needs, and the same transform the markers use.
  const topLeftWorld: Point = {
    x: origin.x - size.width / 2 - pan.x,
    y: origin.y - size.height / 2 - pan.y,
  };
  const tiles = tileUrl ? visibleTiles(topLeftWorld, size.width, size.height, zoom) : [];

  const toScreen = (latitude: number, longitude: number): Point => {
    const point = project(latitude, longitude, zoom);
    return {
      x: point.x - origin.x + size.width / 2 + pan.x,
      y: point.y - origin.y + size.height / 2 + pan.y,
    };
  };

  // Grid spacing that stays roughly 90px apart at any zoom, so the mesh reads
  // as scale rather than decoration.
  const gridStep = 90;
  const scaleMetres = metresPerPixel(center.latitude, zoom) * gridStep;
  const scaleLabel = scaleMetres >= 1000 ? `${(scaleMetres / 1000).toFixed(1)} km` : `${Math.round(scaleMetres)} m`;

  const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    (event.target as Element).setPointerCapture?.(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
  };
  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setPan({ x: drag.panX + (event.clientX - drag.x), y: drag.panY + (event.clientY - drag.y) });
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  return (
    <div className="map-wrap" ref={wrapRef} style={{ height }}>
      <svg
        width={size.width}
        height={size.height}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        role="img"
        aria-label="Situational map of units and incidents"
      >
        <defs>
          <pattern id="cad-grid" width={gridStep} height={gridStep} patternUnits="userSpaceOnUse"
            x={(pan.x % gridStep + gridStep) % gridStep} y={(pan.y % gridStep + gridStep) % gridStep}>
            <path d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="1" />
          </pattern>
        </defs>
        {tiles.map((tile) => (
          <image
            key={tile.key}
            href={tileUrl!
              .replace('{z}', String(tile.z))
              .replace('{x}', String(tile.tx))
              .replace('{y}', String(tile.ty))}
            x={tile.x}
            y={tile.y}
            width={TILE_SIZE}
            height={TILE_SIZE}
            // A tile that fails to load simply leaves the grid showing through.
            onError={(event) => { (event.target as SVGImageElement).style.display = 'none'; }}
          />
        ))}

        {/* The fallback grid. Suppressed once a basemap is in place, where it
            would only add noise over real streets. */}
        {tiles.length === 0 && (
          <rect width={size.width} height={size.height} fill="url(#cad-grid)" className="muted" />
        )}

        {/* Incidents first so unit pins sit on top of the scene they are heading to. */}
        {markers
          .slice()
          .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'incident' ? -1 : 1))
          .map((marker) => {
            const { x, y } = toScreen(marker.latitude, marker.longitude);
            if (x < -40 || y < -40 || x > size.width + 40 || y > size.height + 40) return null;
            const opacity = marker.muted ? 0.35 : 1;

            return (
              <g
                key={`${marker.kind}-${marker.id}`}
                transform={`translate(${x} ${y})`}
                opacity={opacity}
                onClick={() => onSelect?.(marker.kind, marker.id)}
                style={{ cursor: onSelect ? 'pointer' : 'default' }}
              >
                <title>{marker.detail}</title>
                {marker.selected && (
                  <circle r={17} fill="none" stroke={marker.color} strokeWidth={2} strokeDasharray="3 3" />
                )}
                {marker.kind === 'incident' ? (
                  // Incidents are diamonds, units are discs — distinguishable
                  // without relying on colour alone.
                  <rect
                    x={-8} y={-8} width={16} height={16}
                    transform="rotate(45)"
                    fill={marker.color}
                    stroke="rgba(0,0,0,0.5)"
                  />
                ) : (
                  <circle r={9} fill={marker.color} stroke="rgba(0,0,0,0.5)" />
                )}
                <text
                  y={marker.kind === 'incident' ? 24 : 23}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={600}
                  fill="currentColor"
                  stroke="var(--bg)"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {marker.label}
                </text>
              </g>
            );
          })}

        {/* Above the legend on the left: the bottom-right corner belongs to the
            tile attribution, which is a licence condition and cannot be moved. */}
        <g transform={`translate(14 ${size.height - 46})`}>
          <rect x={-6} y={-13} width={gridStep + 62} height={24} rx={4}
            fill="var(--panel)" fillOpacity={0.82} />
          <line x1={0} y1={0} x2={gridStep} y2={0} stroke="var(--text)" strokeWidth={2} />
          <line x1={0} y1={-4} x2={0} y2={4} stroke="var(--text)" strokeWidth={2} />
          <line x1={gridStep} y1={-4} x2={gridStep} y2={4} stroke="var(--text)" strokeWidth={2} />
          <text x={gridStep + 8} y={4} fontSize={11} fill="var(--text)">{scaleLabel}</text>
        </g>
      </svg>

      <div className="map-controls">
        <button type="button" onClick={() => setZoom((z) => Math.min(17, z + 1))} aria-label="Zoom in">+</button>
        <button type="button" onClick={() => setZoom((z) => Math.max(8, z - 1))} aria-label="Zoom out">−</button>
        <button type="button" onClick={() => { setPan({ x: 0, y: 0 }); setZoom(12); }} aria-label="Reset view">⌂</button>
      </div>

      {tileUrl && attribution && (
        <div className="map-attribution">{attribution}</div>
      )}

      <div className="map-legend">
        <span><span className="dot" style={{ background: '#22c55e', display: 'inline-block' }} /> Available</span>
        <span><span className="dot" style={{ background: '#3b82f6', display: 'inline-block' }} /> Committed</span>
        <span>◆ Incident</span>
      </div>
    </div>
  );
}
